import React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Alert from "@mui/material/Alert";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { checkedCircleIcon } from "../../../common/checkedCircleIcon";
import { cancelIconRed } from "../../../common/cancelIconRed";
import { useAppStore } from "../../../../appStore";
import { WireGuardTunnel } from "./SiteAboutPageUtils/siteAboutInterfaces";
import { WireGuardTunnel as AccountWireGuardTunnel } from "../../WireGuardTunnels/wireGuardTunnelsInterfaces";
import { WireGuardTunnelSelect } from "./WireGuardTunnelSelect";
import {
  formatWireguardLastHandshake,
  getSelectedWireguardTunnel,
  resolveWireguardServerEndpoint,
  resolveWireguardServerPublicKey,
} from "./wireguardTabUtils";
import { fetchSiteTunnelUsageData } from "./SiteAboutPageUtils/fetchSiteTunnelUsageData";
import {
  gatewaysShareZone,
  resolveSiteTunnelUsage,
  SiteHost,
  SiteHostSource,
  siteHosts,
  SiteTunnelUsage,
} from "../../../utils/wireguardSiteRouting";

const headCellSx = {
  fontFamily: "Mulish",
  fontWeight: "bold" as const,
};

type WireGuardOption = {
  id: number;
  name: string;
  zone: string;
  server_public_key?: string;
};

type DetailTunnel = WireGuardTunnel | AccountWireGuardTunnel;

/** The site fields tunnel resolution reads (from `customer/{id}/about/`). */
export type WireGuardTabSite = SiteHostSource & {
  wireguard?: number | null;
};

const formatHost = (host: SiteHost) => `${host.label} ${host.address}`;

const TunnelDetailsTable = ({
  tunnel,
  wireGuardOptions,
}: {
  tunnel: DetailTunnel;
  wireGuardOptions: WireGuardOption[];
}) => {
  const endpoint =
    tunnel.server == null
      ? "—"
      : resolveWireguardServerEndpoint(tunnel.server, wireGuardOptions);
  const serverPk =
    tunnel.server == null
      ? "—"
      : resolveWireguardServerPublicKey(tunnel.server, wireGuardOptions);
  const subnetsText = tunnel.subnets?.length ? tunnel.subnets.join(", ") : "—";

  return (
    <Table sx={{ fontFamily: "Mulish" }}>
      <TableBody>
        <TableRow>
          <TableCell sx={headCellSx}>Name</TableCell>
          <TableCell sx={headCellSx}>WireGuard server endpoint</TableCell>
          <TableCell sx={headCellSx}>Server Public Key</TableCell>
          <TableCell sx={headCellSx}>Client Public Key</TableCell>
          <TableCell sx={headCellSx}>Client tunnel IP</TableCell>
        </TableRow>
        <TableRow>
          <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
            {tunnel.name}
          </TableCell>
          <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
            {endpoint}
          </TableCell>
          <TableCell
            align="left"
            sx={{ fontFamily: "Mulish", wordBreak: "break-all" }}
          >
            {serverPk}
          </TableCell>
          <TableCell
            align="left"
            sx={{ fontFamily: "Mulish", wordBreak: "break-all" }}
          >
            {tunnel.client_public_key}
          </TableCell>
          <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
            {tunnel.client_tunnel_ip || "—"}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell colSpan={2} sx={headCellSx}>
            Subnets
          </TableCell>
          <TableCell sx={headCellSx}>Subnets are virtually assigned</TableCell>
          <TableCell sx={headCellSx}>Tunnel status</TableCell>
          <TableCell sx={headCellSx}>Last handshake</TableCell>
        </TableRow>
        <TableRow>
          <TableCell
            colSpan={2}
            align="left"
            sx={{ fontFamily: "Mulish", wordBreak: "break-all" }}
          >
            {subnetsText}
          </TableCell>
          <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
            {tunnel.subnets_are_virtually_assigned
              ? checkedCircleIcon
              : cancelIconRed}
          </TableCell>
          <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
            {tunnel.tunnel_status ?? "—"}
          </TableCell>
          <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
            {formatWireguardLastHandshake(tunnel.last_handshake)}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};

type PendingState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error" };

type UsageState =
  | PendingState
  | {
      status: "ok";
      usage: SiteTunnelUsage<AccountWireGuardTunnel>;
      hostCount: number;
    };

/**
 * Resolve the tunnels this site uses from its addresses (ENG-2056). Needs
 * `siteId` and `site`; without them (legacy callers) nothing is resolved.
 */
const useSiteTunnelUsage = (
  siteId: string | number | null | undefined,
  site: WireGuardTabSite | null | undefined,
  wireGuardOptions: WireGuardOption[]
): UsageState => {
  const [data, setData] = React.useState<
    | PendingState
    | {
        status: "ok";
        cameras: { camera_name: string; ip: string | null }[];
        tunnels: AccountWireGuardTunnel[];
      }
  >({ status: "idle" });

  React.useEffect(() => {
    if (siteId == null || siteId === "" || !site) {
      setData({ status: "idle" });
      return;
    }
    let cancelled = false;
    setData({ status: "loading" });
    fetchSiteTunnelUsageData(siteId)
      .then(({ cameras, tunnels }) => {
        if (!cancelled) setData({ status: "ok", cameras, tunnels });
      })
      .catch(() => {
        if (!cancelled) setData({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
    // Refetch per site, not on every edit of the site record.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, Boolean(site)]);

  return React.useMemo((): UsageState => {
    if (data.status !== "ok") return data;
    const hosts = siteHosts(site, data.cameras);
    const siteGateway = site?.wireguard ?? null;
    const usage = resolveSiteTunnelUsage(hosts, data.tunnels, (server) =>
      gatewaysShareZone(siteGateway, server, wireGuardOptions)
    );
    return { status: "ok", usage, hostCount: hosts.length };
  }, [data, site, wireGuardOptions]);
};

const SiteTunnelUsageSection = ({
  state,
  siteGatewayLabel,
  wireGuardOptions,
}: {
  state: UsageState;
  siteGatewayLabel: string | null;
  wireGuardOptions: WireGuardOption[];
}) => {
  if (state.status === "idle") return null;
  if (state.status === "loading") {
    return (
      <p
        className="font-mulish text-[#283E6D] text-[16px] px-[15px] py-[10px]"
        data-testid="site-tunnel-usage-loading"
      >
        Working out which tunnels this site uses…
      </p>
    );
  }
  if (state.status === "error") {
    return (
      <Alert
        severity="warning"
        sx={{ fontFamily: "Mulish", m: "10px 15px" }}
        data-testid="site-tunnel-usage-error"
      >
        Couldn&apos;t work out which tunnels this site uses.
      </Alert>
    );
  }

  const { usage, hostCount } = state;
  return (
    <div data-testid="site-tunnel-usage" className="px-[15px] py-[10px]">
      {usage.used.length === 0 && (
        <p
          className="font-mulish text-[#283E6D] text-[16px]"
          data-testid="site-tunnel-usage-none"
        >
          {hostCount === 0
            ? "This site has no camera or server addresses to match to a tunnel."
            : usage.hostnames.length === hostCount
            ? "This site's addresses are hostnames, which can't be matched to a tunnel."
            : "No tunnel routes this site's addresses."}
        </p>
      )}
      {usage.used.map(({ tunnel, hosts, otherZone }) => (
        <div
          key={tunnel.id}
          className="mb-4"
          data-testid={`site-tunnel-used-${tunnel.id}`}
        >
          <p className="font-mulish text-[14px] font-bold text-[#001943] mb-1">
            USED BY THIS SITE ({hosts.length}{" "}
            {hosts.length === 1 ? "address" : "addresses"}): {tunnel.name}
          </p>
          <p className="font-mulish text-[14px] text-[#283E6D] mb-1">
            Routes {hosts.map(formatHost).join("; ")}
            {tunnel.subnets_are_virtually_assigned
              ? " (these addresses are in the tunnel's virtual range)"
              : ""}
          </p>
          {otherZone && (
            <Alert
              severity="warning"
              sx={{ fontFamily: "Mulish", mb: 1 }}
              data-testid={`site-tunnel-other-zone-${tunnel.id}`}
            >
              {siteGatewayLabel
                ? `This tunnel routes these addresses but is in a different zone from this site's connector (${siteGatewayLabel}), so the connector can't reach them through it.`
                : "This tunnel routes these addresses, but this site has no WireGuard gateway set, so its connector can't reach them through it."}
            </Alert>
          )}
          <TableContainer component={Paper} variant="outlined">
            <TunnelDetailsTable
              tunnel={tunnel}
              wireGuardOptions={wireGuardOptions}
            />
          </TableContainer>
        </div>
      ))}
      {usage.used.length > 0 && usage.notOnWireGuard.length > 0 && (
        <p
          className="font-mulish text-[14px] text-[#283E6D] mb-1"
          data-testid="site-tunnel-not-on-wireguard"
        >
          Not on WireGuard: {usage.notOnWireGuard.map(formatHost).join("; ")}
        </p>
      )}
      {usage.used.length > 0 && usage.hostnames.length > 0 && (
        <p
          className="font-mulish text-[14px] text-[#283E6D] mb-1"
          data-testid="site-tunnel-hostnames"
        >
          Hostnames can&apos;t be matched to a tunnel:{" "}
          {usage.hostnames.map(formatHost).join("; ")}
        </p>
      )}
    </div>
  );
};

export const WireGuardTab = ({
  wireguardTunnels,
  wireguardTunnelsLoading,
  selectedTunnelId,
  onSelectTunnelId,
  siteId,
  site,
}: {
  wireguardTunnels: WireGuardTunnel[];
  wireguardTunnelsLoading: boolean;
  selectedTunnelId: number | null;
  onSelectTunnelId: (id: number) => void;
  /** With `site`, resolves and lists the tunnels this site actually uses. */
  siteId?: string | number | null;
  site?: WireGuardTabSite | null;
}) => {
  const wireGuardOptions = useAppStore((s) => s.wireGuardOptions);
  const usageState = useSiteTunnelUsage(siteId, site, wireGuardOptions);

  const usedIds = new Set(
    usageState.status === "ok"
      ? usageState.usage.used.map(({ tunnel }) => tunnel.id)
      : []
  );
  const otherTunnels = wireguardTunnels.filter((t) => !usedIds.has(t.id));
  const tunnel = getSelectedWireguardTunnel(otherTunnels, selectedTunnelId);
  const resolving = usageState.status !== "idle";

  const siteGateway = site?.wireguard ?? null;
  const siteGatewayLabel =
    siteGateway == null
      ? null
      : resolveWireguardServerEndpoint(siteGateway, wireGuardOptions);

  if (wireguardTunnelsLoading) {
    return (
      <p className="font-mulish text-[#283E6D] text-[16px]">
        Loading WireGuard tunnels…
      </p>
    );
  }

  if (!resolving && !tunnel) {
    return (
      <p className="font-mulish text-[#283E6D] text-[20px] text-center">
        No WireGuard tunnel data
      </p>
    );
  }

  const otherList = tunnel ? (
    <>
      <WireGuardTunnelSelect
        tunnels={otherTunnels}
        selectedTunnelId={selectedTunnelId}
        onSelectTunnelId={onSelectTunnelId}
      />
      <TunnelDetailsTable tunnel={tunnel} wireGuardOptions={wireGuardOptions} />
    </>
  ) : null;

  if (!resolving) {
    return (
      <TableContainer
        component={Paper}
        sx={{ width: "100%", maxWidth: "100%" }}
      >
        {otherList}
      </TableContainer>
    );
  }

  return (
    <Paper sx={{ width: "100%", maxWidth: "100%" }}>
      <SiteTunnelUsageSection
        state={usageState}
        siteGatewayLabel={siteGatewayLabel}
        wireGuardOptions={wireGuardOptions}
      />
      {/* Keyed on status: remounts when resolution settles, so a failure opens the list. */}
      {otherTunnels.length > 0 && (
        <Accordion
          key={usageState.status}
          disableGutters
          elevation={0}
          defaultExpanded={usageState.status === "error"}
          data-testid="other-account-tunnels"
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <span className="font-mulish text-[14px] font-bold text-[#001943]">
              Other tunnels in this account ({otherTunnels.length})
            </span>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <p className="font-mulish text-[13px] text-[#283E6D] px-[15px]">
              A site uses whichever tunnel routes its camera addresses
              {usageState.status === "ok"
                ? ", so these tunnels don't carry this site."
                : "."}
            </p>
            {otherList}
          </AccordionDetails>
        </Accordion>
      )}
    </Paper>
  );
};
